import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

interface BulkOperationsProps {
  selectedSampleIds: Set<number>;
  onBulkDispose: () => void | Promise<void>;
  onBulkDelete?: () => void | Promise<void>;
  onBulkUsageRecord: () => void;
  onClearSelection: () => void;
  isDisposing?: boolean;
  isDeleting?: boolean;
  isAddingUsage?: boolean;
}

type BulkAction = {
  key: string;
  variant: 'success' | 'warning' | 'danger';
  icon: string;
  shortLabel: string;
  title: string;
  busy: boolean;
  onClick: () => void;
};

/** 選択中サンプルへの一括操作バー（件数・解除・使用／廃棄／削除） */
export default function BulkOperations({
  selectedSampleIds,
  onBulkDispose,
  onBulkDelete,
  onBulkUsageRecord,
  onClearSelection,
  isDisposing = false,
  isDeleting = false,
  isAddingUsage = false,
}: BulkOperationsProps) {
  const { t } = useTranslation();
  const count = selectedSampleIds.size;
  if (count === 0) return null;

  const busy = isDisposing || isDeleting || isAddingUsage;
  const clearShort = t('sample.clear_selection_short', {
    defaultValue: t('sample.clear_selection'),
  });

  const actions: BulkAction[] = [
    {
      key: 'usage',
      variant: 'success',
      icon: 'fa-clipboard-list',
      shortLabel: t('usage_record.bulk_add_usage_short', {
        defaultValue: t('usage_record.bulk_add_usage'),
      }),
      title: t('usage_record.bulk_add_usage'),
      busy: isAddingUsage,
      onClick: onBulkUsageRecord,
    },
    {
      key: 'dispose',
      variant: 'warning',
      icon: 'fa-archive',
      shortLabel: t('sample.bulk_dispose_short', { defaultValue: t('sample.bulk_dispose') }),
      title: t('sample.bulk_dispose'),
      busy: isDisposing,
      onClick: onBulkDispose,
    },
  ];

  if (onBulkDelete) {
    actions.push({
      key: 'delete',
      variant: 'danger',
      icon: 'fa-trash-alt',
      shortLabel: t('sample.bulk_delete_short', { defaultValue: t('sample.bulk_delete') }),
      title: t('sample.bulk_delete'),
      busy: isDeleting,
      onClick: onBulkDelete,
    });
  }

  return (
    <div
      className="sample-bulk-bar"
      role="toolbar"
      aria-label={t('sample.selected_count', { count })}
    >
      <span className="sample-bulk-count">{t('sample.selected_count', { count })}</span>
      <Button
        variant="outline-secondary"
        size="sm"
        className="sample-bulk-clear"
        onClick={onClearSelection}
        disabled={busy}
        aria-label={clearShort}
        title={t('sample.clear_selection')}
      >
        {clearShort}
      </Button>
      <div className="sample-bulk-actions">
        {actions.map((action) => (
          <Button
            key={action.key}
            variant={action.variant}
            size="sm"
            className="sample-bulk-action"
            onClick={action.onClick}
            disabled={busy}
            title={action.title}
            aria-label={action.shortLabel}
          >
            {action.busy ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            ) : (
              <i className={`fas ${action.icon}`} aria-hidden="true" />
            )}
            <span className="sample-bulk-label">{action.shortLabel}</span>
          </Button>
        ))}
      </div>
    </div>
  );
}
